import { parseDate } from '@internationalized/date';
import { Spacing } from '@persona-id/design-tokens';
import { startCase } from 'lodash/fp';
import styled from 'styled-components';

import ReactSelect, { OptionType } from 'dashboard/components/ReactSelect';
import MaximizablePanel from 'dashboard/components-lib/MaximizablePanel';
import { GovernmentIdExtractions } from 'dashboard/models/simulate/stubs';
import DateField from 'design-system/components/DatePicker/DateField';
import { Input } from 'design-system/components/Input';
import { Label } from 'design-system/components/Text';
import { GOVERNMENT_ID_DISPLAY_STRINGS, GovernmentIdClass } from 'lib/constants/governmentIds';
import { COUNTRIES, CountryCode } from 'lib/constants/locales';
import { assertNever } from 'lib/utils/types';

const StyledLabel = styled(Label)`
  display: inline-block;
  font-weight: ${({ theme }) => theme.text.paragraph.mdBold.fontWeight};
  margin-bottom: 0px;
`;

const StyledInput = styled(Input).attrs(() => ({ size: 'md' }))`
  margin-bottom: ${Spacing[2]};
`;

const StyledSelectInput = styled(ReactSelect).attrs(() => ({ size: 'md' }))`
  margin-bottom: ${Spacing[2]};
`;

const StyledDateInput = styled(DateField)`
  margin-bottom: ${Spacing[2]};
`;

const COUNTRY_OPTIONS: OptionType<CountryCode>[] = Object.keys(COUNTRIES).map((option: string) => ({
  value: option as CountryCode,
  label: `${COUNTRIES[option as CountryCode]} (${option})`,
}));

const ID_CLASS_OPTIONS: OptionType<GovernmentIdClass>[] = Object.keys(
  GOVERNMENT_ID_DISPLAY_STRINGS,
).map((option: string) => ({
  value: option as Exclude<GovernmentIdClass, undefined>,
  label: `${GOVERNMENT_ID_DISPLAY_STRINGS[option as Exclude<GovernmentIdClass, undefined>]} (${option})`,
}));

export enum ExtractionType {
  String = 'string',
  Date = 'date',
}

const GOV_ID_EXTRACTION_TO_TYPE = {
  birthdate: ExtractionType.Date,
  'identification-number': ExtractionType.String,
  'id-class': ExtractionType.String,
  'name-first': ExtractionType.String,
  'name-last': ExtractionType.String,
  'address-street-1': ExtractionType.String,
  'address-city': ExtractionType.String,
  'address-subdivision': ExtractionType.String,
  'address-postal-code': ExtractionType.String,
  'issuing-authority': ExtractionType.String,
  'issuing-subdivision': ExtractionType.String,
  'country-code': ExtractionType.String,
  sex: ExtractionType.String,
  'issue-date': ExtractionType.Date,
  'expiration-date': ExtractionType.Date,
};

const ExtractionStubComponent = ({
  name,
  type,
  setGovernmentIdExtractionStubs,
  value,
  trackEvent,
}: {
  name: string;
  type: ExtractionType;
  setGovernmentIdExtractionStubs: ({ key, value }: { key: string; value: any }) => void;
  value: any;
  trackEvent: (eventName: string, props?: Record<string, any>) => void;
}) => {
  if (name === 'country-code') {
    return (
      <>
        <StyledLabel htmlFor={`extractions__${name}-input`} size='sm'>
          {startCase(name)}
        </StyledLabel>
        <StyledSelectInput
          id={`extractions__${name}`}
          value={value}
          options={COUNTRY_OPTIONS}
          portal={true}
          onChange={(value: string) => {
            setGovernmentIdExtractionStubs({
              key: name,
              value: value,
            });
            trackEvent('Flow Editor - Preview Document Stub Changed', {
              document_stub: name,
            });
          }}
        />
      </>
    );
  } else if (name === 'id-class') {
    return (
      <>
        <StyledLabel htmlFor={`extractions__${name}-input`} size='sm'>
          {startCase(name)}
        </StyledLabel>
        <StyledSelectInput
          id={`extractions__${name}`}
          value={value}
          options={ID_CLASS_OPTIONS}
          portal={true}
          onChange={(value: string) => {
            setGovernmentIdExtractionStubs({
              key: name,
              value: value,
            });
            trackEvent('Flow Editor - Preview Document Stub Changed', {
              document_stub: name,
            });
          }}
        />
      </>
    );
  }

  switch (type) {
    case ExtractionType.Date:
      return (
        <>
          <StyledLabel htmlFor={`extractions__${name}-input`} size='sm'>
            {startCase(name)}
          </StyledLabel>
          <StyledDateInput
            id={`extractions__${name}`}
            value={value && parseDate(value)}
            onChange={(value) => {
              setGovernmentIdExtractionStubs({
                key: name,
                value: value && value.toString(),
              });
              trackEvent('Flow Editor - Preview Document Stub Changed', {
                document_stub: name,
              });
            }}
          />
        </>
      );
    case ExtractionType.String:
      return (
        <>
          <StyledLabel htmlFor={`extractions__${name}`} size='sm'>
            {startCase(name)}
          </StyledLabel>
          <StyledInput
            id={`extractions__${name}`}
            value={value}
            onChange={(e) => {
              setGovernmentIdExtractionStubs({
                key: name,
                value: e.target.value,
              });
              trackEvent('Flow Editor - Preview Document Stub Changed', {
                document_stub: name,
              });
            }}
          />
        </>
      );
    default:
      assertNever(type as never);
  }
};

interface GovernmentIdDocumentStubsPanelProps {
  governmentIdExtractionStubs: GovernmentIdExtractions;
  setGovernmentIdExtractionStubs: ({ key, value }: { key: string; value: any }) => void;
  trackEvent: (eventName: string, props?: Record<string, any>) => void;
  offsetElementId?: string;
  onOutsideClick: () => void;
}

export const GovernmentIdDocumentStubsPanel = ({
  governmentIdExtractionStubs,
  setGovernmentIdExtractionStubs,
  onOutsideClick,
  offsetElementId,
  trackEvent,
}: GovernmentIdDocumentStubsPanelProps) => {
  return (
    <MaximizablePanel
      title='ID Details'
      subtitle='Simulate extractions by specifying ID details.'
      onOutsideClickAndEscPress={onOutsideClick}
      offsetElementId={offsetElementId}
      outsideExceptionalIdPrefixes={['extractions']}
    >
      {Object.entries(GOV_ID_EXTRACTION_TO_TYPE).map(([name, type], idx) => {
        return (
          <div key={idx} role='listitem'>
            <ExtractionStubComponent
              name={name}
              type={type}
              setGovernmentIdExtractionStubs={setGovernmentIdExtractionStubs}
              value={governmentIdExtractionStubs[name as keyof GovernmentIdExtractions]}
              trackEvent={trackEvent}
            />
          </div>
        );
      })}
    </MaximizablePanel>
  );
};
